import { useMemo, useState } from 'react';
import { TYPE_LABEL, day, rupee } from './financeConfig';
import styles from './supFinance.module.css';

/*
 * A finance report table: sortable headers, totals row, empty state.
 *   columns: COLUMNS[...] from financeConfig
 *   onBill(row): "Bill" button for payout rows
 */
const STATUS_CLASS = { 'Refund Initiated': 'warn', 'Refund Failed': 'bad', Refunded: 'good' };

function cell(col, row, onBill) {
    const v = row[col.key];
    switch (col.kind) {
        case 'money':
            return rupee(v);
        case 'date':
            return day(v);
        case 'pct':
            return v === null || v === undefined ? '—' : `${v}%`;
        case 'num':
            return Number(v || 0).toLocaleString('en-IN');
        case 'type':
            return TYPE_LABEL[v] || v || '—';
        case 'id':
            return v ? <span className={styles.mono}>{String(v).slice(-8)}</span> : '—';
        case 'gstin':
            return v ? <span className={styles.mono}>{v}</span> : <span className={styles.missing}>Missing</span>;
        case 'status':
            return <span className={`${styles.pill} ${styles[STATUS_CLASS[v] || 'neutral']}`}>{v || '—'}</span>;
        case 'bill':
            return (
                <button type='button' className={styles.linkBtn} onClick={() => onBill?.(row)}>
                    Bill
                </button>
            );
        default:
            return v ?? '—';
    }
}

const numeric = (kind) => ['money', 'num', 'pct'].includes(kind);

export default function FinanceTable({ columns, rows = [], onBill, empty = 'Nothing in this period.' }) {
    const [sort, setSort] = useState({ key: null, dir: -1 });

    const sorted = useMemo(() => {
        if (!sort.key) return rows;
        return [...rows].sort((a, b) => {
            const x = a[sort.key];
            const y = b[sort.key];
            if (typeof x === 'number' || typeof y === 'number') return ((x || 0) - (y || 0)) * sort.dir;
            return String(x || '').localeCompare(String(y || '')) * sort.dir;
        });
    }, [rows, sort]);

    const totals = useMemo(() => {
        const t = {};
        columns
            .filter((c) => c.total)
            .forEach((c) => (t[c.key] = rows.reduce((s, r) => s + (Number(r[c.key]) || 0), 0)));
        return t;
    }, [rows, columns]);

    if (!rows.length) return <p className={styles.empty}>{empty}</p>;

    return (
        <div className={styles.tableWrap}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        {columns.map((c) => (
                            <th
                                key={c.key}
                                className={numeric(c.kind) ? styles.num : ''}
                                onClick={() =>
                                    c.kind !== 'bill' &&
                                    setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : -1 }))
                                }
                            >
                                {c.label}
                                {sort.key === c.key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((row, i) => (
                        <tr key={row._id || row.bookingId || row.payoutId || i}>
                            {columns.map((c) => (
                                <td key={c.key} className={numeric(c.kind) ? styles.num : ''}>
                                    {cell(c, row, onBill)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        {columns.map((c, i) => (
                            <td key={c.key} className={numeric(c.kind) ? styles.num : ''}>
                                {i === 0
                                    ? `Total (${rows.length})`
                                    : c.total
                                      ? c.kind === 'money'
                                          ? rupee(totals[c.key])
                                          : totals[c.key].toLocaleString('en-IN')
                                      : ''}
                            </td>
                        ))}
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}
